import React from 'react';

import { CompletionStatus } from '@eika-felles/kyc-declaration-lib/dist/generated-types';

import {
    FormTypeApplicationCoApplicant,
    FormTypeApplicationMainApplicant,
    getOrCreateSblConsent,
    kycError,
    loanApplicationMainApplicantKycNextEvent,
    loanApplicationMainApplicantNextEvent,
    loanApplicationSecondApplicantNextEvent,
    loanApplicationSecondApplicantSkipNextEvent,
    openStep,
    sblFailed,
    setKycAnswered,
    skipKyc,
    toggleCoApplicant,
} from '../../actions';
import { fetchCalculatorCache } from '../../actions/calculatorCache';
import { DispatchWithState } from '../../actions/types';
import ExpandableTableItems from '../../components/LoanApplication/ExpandableTableItems';
import ProductImage from '../../components/ProductImage/ProductImage';
import Step from '../../components/Steps/Step';
import StepCounter from '../../components/Steps/StepCounter';
import Steps from '../../components/Steps/Steps';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { getProductApplicationTitle, getProductIcon } from '../../util/mappings';
import LoanApplicantMain from './LoanApplicantMain/LoanApplicantMain';
import LoanApplicantMainKyc from './LoanApplicantMainKyc/LoanApplicantMainKyc';
import LoanApplicantSecond from './LoanApplicantSecond/LoanApplicantSecond';
import LoanApplicationSbl from './LoanApplicationSbl/LoanApplicationSbl';
import LoanInfo from './LoanInfo/LoanInfo';

const LoanApplication: React.FC = () => {
    const dispatch = useAppDispatch();

    const text = useAppSelector((s) => s.context.config.data.text);
    const form = useAppSelector((s) => s.context.config.data.form);
    const application = useAppSelector((s) => s.application);
    const calculator = useAppSelector((s) => s.calculator);
    const loader = useAppSelector((s) => s.loader);
    const sbl = useAppSelector((s) => s.sbl);
    const steps = useAppSelector((s) => s.steps);
    const user = useAppSelector((s) => s.user);
    const feature = useAppSelector((s) => s.feature);

    const action = {
        loanApplicationMainApplicantNextEvent: (f: FormTypeApplicationMainApplicant): DispatchWithState =>
            dispatch(
                loanApplicationMainApplicantNextEvent(f) as unknown as DispatchWithState,
            ) as unknown as DispatchWithState,
        loanApplicationMainApplicantKycNextEvent: (): DispatchWithState =>
            dispatch(
                loanApplicationMainApplicantKycNextEvent() as unknown as DispatchWithState,
            ) as unknown as DispatchWithState,
        loanApplicationSecondApplicantNextEvent: (f: FormTypeApplicationCoApplicant): DispatchWithState =>
            dispatch(
                loanApplicationSecondApplicantNextEvent(f) as unknown as DispatchWithState,
            ) as unknown as DispatchWithState,
        loanApplicationSecondApplicantSkipNextEvent: (): DispatchWithState =>
            dispatch(
                loanApplicationSecondApplicantSkipNextEvent() as unknown as DispatchWithState,
            ) as unknown as DispatchWithState,
        toggleCoApplicant: (hasCoApplicant: boolean): void => {
            dispatch(toggleCoApplicant(hasCoApplicant));
        },
        fetchCalculatorCache: (): DispatchWithState =>
            dispatch(fetchCalculatorCache() as unknown as DispatchWithState) as unknown as DispatchWithState,
        getOrCreateSblConsent: (): DispatchWithState =>
            dispatch(getOrCreateSblConsent() as unknown as DispatchWithState) as unknown as DispatchWithState,
        openStep: (destinationStep: number): void => {
            dispatch(openStep(destinationStep));
        },
        setKycAnswered: (): void => {
            dispatch(setKycAnswered());
        },
        kycError: (): void => {
            dispatch(kycError());
        },
        skipKyc: (): void => {
            dispatch(skipKyc());
        },
        sblFailed: (): unknown => dispatch(sblFailed()),
    };

    const { loanInfo } = calculator;
    const { loanType, loanProduct } = loanInfo;
    const { currentStep } = steps;
    const applicationTitle = getProductApplicationTitle(loanType, loanProduct, text);

    return (
        <div className="loan-application">
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <>
                    {currentStep === 1 ? (
                        <ProductImage
                            className="loan-application__intro"
                            image={getProductIcon(loanType, loanProduct)}
                            title={applicationTitle}
                        />
                    ) : (
                        <h1 className="text-center">{applicationTitle}</h1>
                    )}

                    <Steps>
                        <Step open={currentStep === 1}>
                            <StepCounter currentStep="1" totalSteps="5" />
                            <LoanInfo
                                action={action}
                                calculator={calculator}
                                loader={loader}
                                feature={feature}
                                text={text}
                            />
                        </Step>
                        <Step open={currentStep === 2}>
                            <StepCounter currentStep="2" totalSteps="5" />
                            <LoanApplicationSbl action={action} sbl={sbl} loader={loader} text={text} />
                        </Step>
                        <Step open={currentStep === 3}>
                            <StepCounter currentStep="3" totalSteps="5" />
                            <LoanApplicantMain
                                action={action}
                                application={application}
                                calculator={calculator}
                                feature={feature}
                                loader={loader}
                                form={form}
                                text={text}
                            />
                        </Step>
                        <Step open={currentStep === 4}>
                            <StepCounter currentStep="4" totalSteps="5" />
                            <LoanApplicantMainKyc
                                ssn={user.context.ssn}
                                previousEvent={() => action.openStep(3)}
                                completeEvent={(status: string) => {
                                    if (status === CompletionStatus.FAILED) action.kycError();
                                    else if (
                                        status === CompletionStatus.OK ||
                                        status === CompletionStatus.REQUIRES_MANUAL_CONTROL_BY_ADVISOR
                                    ) {
                                        action.setKycAnswered();
                                        action.loanApplicationMainApplicantKycNextEvent();
                                    }
                                }}
                                skipEvent={() => {
                                    action.skipKyc();
                                    action.loanApplicationMainApplicantKycNextEvent();
                                }}
                                skipKycEnabled={feature.salgspantSkipKyc}
                                text={text}
                                loader={loader}
                            />
                        </Step>

                        <Step open={currentStep === 5}>
                            <StepCounter currentStep="4" totalSteps="5" />
                            <LoanApplicantSecond
                                action={action}
                                application={application}
                                loader={loader}
                                calculator={calculator}
                                form={form}
                                text={text}
                            />
                        </Step>
                    </Steps>

                    <ExpandableTableItems text={text} />
                </>
            </div>
        </div>
    );
};

export default LoanApplication;
